import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  CheckCircle, UserPlus, Sparkle, ShieldCheck, Handshake, SealCheck,
  MagnifyingGlass, ArrowRight, UploadSimple, FileText, X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import PhoneInput from '@/components/PhoneInput';

import { API } from '@/config/api';

// Vetting stages mirror the Home page funnel — kept in sync deliberately so
// the story reads the same whether a recruiter lands here from Home or via
// a direct link.
const VETTING = [
  { icon: MagnifyingGlass, title: 'Written application',    desc: 'Everything below plus a short portfolio of past placements.' },
  { icon: Handshake,       title: 'Interview & references', desc: 'Call with our leadership and reference checks with students & colleges.' },
  { icon: ShieldCheck,     title: 'Compliance review',      desc: 'PTIB, IRCC, provincial rules. One red flag and we decline. No exceptions.' },
  { icon: SealCheck,       title: 'Signed agreement',       desc: 'A binding partnership contract with our code of conduct. Annually renewed.' },
];

const LICENSE_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const LICENSE_MAX_BYTES = 10 * 1024 * 1024;
const LICENSE_ALLOWED_EXT = ['pdf', 'jpg', 'jpeg', 'png'];

const formatBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export default function RecruiterSignUp() {
  const [form, setForm] = useState({
    full_name: '', company_name: '', email: '', phone: '', phone_code: '+1',
    country: '', city: '', years_of_experience: '', message: '',
    terms_accepted: false, guidelines_accepted: false,
  });
  const [licenseFile, setLicenseFile] = useState(null);
  const [licenseError, setLicenseError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleLicenseSelect = (file) => {
    setLicenseError('');
    if (!file) { setLicenseFile(null); return; }
    const name = file.name || '';
    const ext = (name.includes('.') ? name.split('.').pop() : '').toLowerCase();
    if (!LICENSE_ALLOWED_EXT.includes(ext)) {
      setLicenseFile(null);
      setLicenseError('Please upload a PDF, JPG, or PNG file.');
      return;
    }
    if (file.size > LICENSE_MAX_BYTES) {
      setLicenseFile(null);
      setLicenseError('File is too large. Max size is 10 MB.');
      return;
    }
    setLicenseFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.terms_accepted) { toast.error('Please accept the terms to continue.'); return; }
    if (!form.guidelines_accepted) { toast.error('Please agree to the Recruiter Guidelines to continue.'); return; }
    if (!licenseFile) {
      setLicenseError('Business license is required.');
      toast.error('Please upload your business license (PDF, JPG, or PNG).');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('full_name', form.full_name);
      fd.append('company_name', form.company_name);
      fd.append('email', form.email);
      fd.append('phone', `${form.phone_code} ${form.phone}`);
      fd.append('country', form.country);
      fd.append('city', form.city);
      fd.append('years_of_experience', form.years_of_experience);
      fd.append('message', form.message || '');
      fd.append('terms_accepted', String(form.terms_accepted));
      fd.append('guidelines_accepted', String(form.guidelines_accepted));
      fd.append('business_license', licenseFile);
      await axios.post(`${API}/recruiters/signup`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(true);
      toast.success('Application submitted — we\u2019ll be in touch.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900" data-testid="signup-success">
        <div className="text-center max-w-lg text-white">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
            <CheckCircle size={30} weight="fill" className="text-emerald-300" />
          </div>
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur">
            <Sparkle size={12} weight="fill" className="text-amber-300" />
            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/90">Application Received</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Libre Baskerville', serif" }}>Thank you. We&rsquo;ll review this personally.</h2>
          <p className="text-blue-100/85 mb-2">Our team reads every application by hand — no auto-filters, no bots.</p>
          <p className="text-blue-100/70 mb-8 text-sm">Whether it&rsquo;s a yes or a no, we&rsquo;ll reply within <strong className="text-white">48 hours</strong>. If we move you to interview, we&rsquo;ll share the next steps in that reply.</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-white text-blue-900 px-6 py-3 rounded-md font-semibold hover:bg-blue-50 transition-all hover:-translate-y-0.5" data-testid="back-home-btn">
            Back to Home <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="recruiter-signup-page">
      {/* Hero — dark, exclusive framing */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 text-white">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-20 w-96 h-96 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur">
              <Sparkle size={14} weight="fill" className="text-amber-300" />
              <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/90">Request Recruiter Invite</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              Apply to be one of the<br />
              <span className="text-blue-300">recruiters we work with.</span>
            </h1>
            <p className="text-lg text-blue-100/85 leading-relaxed mb-3 max-w-2xl">
              Study Alliance is small on purpose. We work with a curated circle of ethical, experienced recruiters — not everyone who applies.
            </p>
            <p className="text-sm text-blue-100/70 mb-2">
              Fill in the form below. Our team reviews every submission manually and replies within <strong className="text-white">48 hours</strong>, whether it&rsquo;s a yes or a no.
            </p>
            <p className="text-[11px] uppercase tracking-[0.14em] text-amber-300/90 font-bold mt-6">
              Roughly 1 in 12 applications is approved.
            </p>
          </div>
        </div>
      </section>

      {/* Vetting funnel snapshot */}
      <section className="border-b border-gray-100 bg-slate-50/60" data-testid="signup-vetting-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-2xl mb-8">
            <p className="text-blue-700 font-bold text-[11px] uppercase tracking-[0.16em] mb-2">What to Expect</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              Four stages. All of them handled by humans.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {VETTING.map((v, i) => (
              <div key={v.title} className="relative rounded-xl p-5 bg-white border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all">
                <div className="absolute top-3 right-3 text-3xl font-bold text-gray-100 tabular-nums" style={{ fontFamily: "'Libre Baskerville', serif" }}>0{i + 1}</div>
                <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 text-blue-700 flex items-center justify-center mb-3">
                  <v.icon size={18} weight="fill" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1.5" style={{ fontFamily: "'Libre Baskerville', serif" }}>{v.title}</h3>
                <p className="text-xs text-gray-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 pb-6 border-b border-gray-100">
            <p className="text-blue-700 font-bold text-[11px] uppercase tracking-[0.16em] mb-2">Stage 1 of 4 · Written Application</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              Tell us who you are and how you place.
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              The stronger your write-up under &ldquo;Portfolio &amp; notes&rdquo;, the faster we can move to Stage 2. Concrete numbers (students placed, colleges, retention) always help.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="recruiter-signup-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input id="full_name" value={form.full_name} onChange={e => handleChange('full_name', e.target.value)} required data-testid="signup-fullname-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_name">Company / Agency Name *</Label>
                <Input id="company_name" value={form.company_name} onChange={e => handleChange('company_name', e.target.value)} required data-testid="signup-company-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="email">Business Email *</Label>
                <Input id="email" type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} required data-testid="signup-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <PhoneInput
                  id="phone"
                  value={form.phone}
                  onChange={e => handleChange('phone', e.target.value)}
                  countryCode={form.phone_code}
                  onCountryCodeChange={v => handleChange('phone_code', v)}
                  data-testid="signup-phone-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Input id="country" value={form.country} onChange={e => handleChange('country', e.target.value)} required data-testid="signup-country-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input id="city" value={form.city} onChange={e => handleChange('city', e.target.value)} required data-testid="signup-city-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="experience">Years of experience in student recruitment *</Label>
              <Input id="experience" value={form.years_of_experience} onChange={e => handleChange('years_of_experience', e.target.value)} placeholder="e.g., 3 years" required data-testid="signup-experience-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Portfolio &amp; notes</Label>
              <Textarea id="message" rows={5} value={form.message} onChange={e => handleChange('message', e.target.value)} placeholder="Roughly how many students have you placed? Which colleges/regions? Are you currently working with any accreditor or regulator? Anything else we should know." data-testid="signup-message-input" />
              <p className="text-[11px] text-gray-500">Optional but strongly recommended — this is the section our team reads most carefully.</p>
            </div>

            {/* Business license — mandatory */}
            <div className="space-y-2" data-testid="signup-business-license-section">
              <Label htmlFor="business_license">
                Business License <span className="text-red-600">*</span>
              </Label>
              <p className="text-[11px] text-gray-500 -mt-1">
                Upload a scan or clear photo of your agency&rsquo;s registered business license. PDF, JPG or PNG · max 10 MB.
              </p>
              {!licenseFile ? (
                <label
                  htmlFor="business_license"
                  className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 cursor-pointer transition-colors ${licenseError ? 'border-red-300 bg-red-50/50 hover:bg-red-50' : 'border-gray-300 bg-slate-50/60 hover:bg-slate-100'}`}
                  data-testid="signup-license-dropzone"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                    <UploadSimple size={18} weight="bold" />
                  </div>
                  <p className="text-sm font-medium text-gray-800">Click to upload your business license</p>
                  <p className="text-[11px] text-gray-500">PDF, JPG or PNG — up to 10 MB</p>
                  <input
                    id="business_license"
                    type="file"
                    accept={LICENSE_ACCEPT}
                    className="sr-only"
                    onChange={(e) => handleLicenseSelect(e.target.files?.[0] || null)}
                    data-testid="signup-license-input"
                  />
                </label>
              ) : (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3" data-testid="signup-license-preview">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-white border border-emerald-200 text-emerald-700 flex items-center justify-center shrink-0">
                      <FileText size={16} weight="bold" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate" title={licenseFile.name}>{licenseFile.name}</p>
                      <p className="text-[11px] text-gray-500">{formatBytes(licenseFile.size)} · ready to upload</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setLicenseFile(null); setLicenseError(''); }}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-md hover:bg-white/60"
                    aria-label="Remove file"
                    data-testid="signup-license-remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              {licenseError && (
                <p className="text-xs text-red-600" data-testid="signup-license-error">{licenseError}</p>
              )}
            </div>

            {/* Compliance checkboxes */}
            <div className="rounded-xl border border-gray-200 bg-slate-50/60 p-4 space-y-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={form.terms_accepted}
                  onCheckedChange={(checked) => handleChange('terms_accepted', checked)}
                  data-testid="signup-terms-checkbox"
                />
                <Label htmlFor="terms" className="text-sm text-gray-700 cursor-pointer leading-relaxed">
                  I confirm the information above is accurate and consent to Study Alliance processing my application. I understand my account requires manual review and approval before any access is granted.
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="guidelines"
                  checked={form.guidelines_accepted}
                  onCheckedChange={(checked) => handleChange('guidelines_accepted', checked)}
                  data-testid="signup-guidelines-checkbox"
                />
                <Label htmlFor="guidelines" className="text-sm text-gray-700 cursor-pointer leading-relaxed">
                  I have read and agree to the{' '}
                  <Link
                    to="/recruiter-guidelines"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 font-medium hover:underline"
                    data-testid="signup-guidelines-link"
                  >
                    Recruiter Guidelines
                  </Link>
                  , including the strict StudentAid BC &ldquo;student-led only&rdquo; rule, the requirement to submit only truthful and verified information, and Study Alliance&rsquo;s compliance and enforcement policies. A timestamped record of my acceptance will be kept on file.
                </Label>
              </div>
            </div>

            <div className="pt-2">
              <Button type="submit" disabled={loading} className="w-full bg-blue-800 hover:bg-blue-900 text-white h-12 shadow-md shadow-blue-800/20" data-testid="signup-submit-btn">
                <UserPlus size={18} className="mr-2" />
                {loading ? 'Submitting…' : 'Submit for Review'}
              </Button>
              <p className="text-center text-[11px] text-gray-500 mt-3">
                Manual review · reply within 48 hours · references may be contacted
              </p>
            </div>

            <p className="text-center text-sm text-gray-500 pt-4 border-t border-gray-100">
              Already partnered with us? <Link to="/login" className="text-blue-700 hover:underline font-medium" data-testid="signup-login-link">Log in here</Link>
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
